import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'ghost';
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  actions?: ToastAction[];
}

interface AppToastProps {
  toast: Toast;
  onClose: (id: string) => void;
  index?: number;
}

const AppToastComponent: React.FC<AppToastProps> = ({ toast, onClose, index = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Animação de entrada
    const enterTimer = setTimeout(() => setIsVisible(true), 10);
    
    // Auto-fechar
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose(toast.id), 300);
    }, toast.duration || 4000);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(timer);
    };
  }, [toast.id, toast.duration]);

  const getTypeConfig = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-emerald-600',
          icon: <CheckCircle size={20} />,
          border: 'border-emerald-700',
        };
      case 'error':
        return {
          bg: 'bg-red-600',
          icon: <AlertCircle size={20} />,
          border: 'border-red-700',
        };
      case 'warning':
        return {
          bg: 'bg-amber-600',
          icon: <AlertTriangle size={20} />,
          border: 'border-amber-700',
        };
      default: // info
        return {
          bg: 'bg-blue-600',
          icon: <Info size={20} />,
          border: 'border-blue-700',
        };
    }
  };

  const config = getTypeConfig();

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      data-toast-type={toast.type}
      className={`fixed top-4 right-4 ${config.bg} text-white px-5 py-3.5 rounded shadow-lg z-[150] flex items-center gap-3 min-w-[300px] max-w-[500px] border-l-4 ${config.border} transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
      style={{ marginTop: `${index * 80}px` }}
    >
      <div className="flex-shrink-0">{config.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm leading-tight">{toast.message}</p>
        {toast.actions && toast.actions.length > 0 && (
          <div className="flex items-center gap-2 mt-2">
            {toast.actions.map((action, i) => (
              <button
                key={i}
                onClick={() => {
                  action.onClick();
                  setIsVisible(false);
                  setTimeout(() => onClose(toast.id), 300);
                }}
                className={`text-xs font-semibold px-2.5 py-1 rounded transition-colors ${
                  action.variant === 'ghost'
                    ? 'bg-white/10 hover:bg-white/20 text-white'
                    : 'bg-white text-gray-900 hover:bg-gray-100'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => onClose(toast.id), 300);
        }}
        className="flex-shrink-0 hover:bg-white/20 rounded p-1 transition-colors self-start"
        aria-label="Fechar"
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Hook para gerenciar toasts
export const useAppToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((type: ToastType, message: string, duration?: number) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = { id, type, message, duration };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const showActionToast = useCallback(
    (type: ToastType, message: string, actions: ToastAction[], duration?: number) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const newToast: Toast = { id, type, message, duration: duration ?? 8000, actions };
      setToasts((prev) => [...prev, newToast]);
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const ToastContainer = useMemo<React.FC>(
    () =>
      function ToastContainerComponent() {
        return (
          <>
            {toasts.map((toast, index) => (
              <AppToastComponent
                key={toast.id}
                toast={toast}
                onClose={removeToast}
                index={index}
              />
            ))}
          </>
        );
      },
    [toasts, removeToast]
  );

  return {
    showToast,
    showActionToast,
    ToastContainer,
  };
};
